import "./LinkPageElement-style";
import "./PagePickerElement";
import "./SearchSummaryElement";
import "./StackLinkElement";
import { AuthenticationStore } from "features/authentication";
import { BulkLinkStore } from "features/link-list/BulkLinkStore";
import { ElementDataWatcher } from "lib/ElementDataWatcher";
import { Formatting } from "lib/Formatting";
import { LinkPage, LinkPageCriteria } from "./LinkPageModel";
import { LinkSession } from "./LinkSession";
import { LinkPageStore } from "./LinkPageStore";
import { Link } from "features/links";
import { Linkstack, StackStore } from "features/stacks/StackStore";
import { Routing } from "lib/Routing";
import { SerializedEventQueue } from "lib/SerializedEventQueue";

export class LinkPageElement extends HTMLElement {
    private userId: string;
    private escape = Formatting.htmlEscape;
    private criteriaWatcher: ElementDataWatcher<LinkPageCriteria>;
    private serializedEventQueue = new SerializedEventQueue();
    private linkArea: HTMLElement;
    private showStackIndicators = false;
    private jumpToLinkId: string;

    connectedCallback() {
        // init data
        this.criteriaWatcher = new ElementDataWatcher<LinkPageCriteria>(this, "link-page-criteria");

        // just trying without async callback

        requestAnimationFrame(async () => {
            // are we at the right page to jump to link?
            if (!await this.scrollCheck())
                return;

            this.userId = (await AuthenticationStore.instance.currentUser()).userId;

            if (!this.hasAttribute("rendered"))
                await this.render();

            await this.scrollToElement();
        });


        // grab elements
        this.linkArea = this.querySelector("[name=link-area]");

        // setup events
        this.addEventListener("selected", this.itemSelectHandler);
        this.addEventListener("deselected", this.itemDeselectHandler);

        BulkLinkStore.instance.addEventListener("select-all-requested", this.selectAllHandler);
        BulkLinkStore.instance.addEventListener("selected", this.selectHandler);
        BulkLinkStore.instance.addEventListener("deselected", this.deselectHandler);

        LinkPageStore.instance.addEventListener("link-added", this.linkAddedHandler);
        LinkPageStore.instance.addEventListener("link-updated", this.linkUpdatedHandler);
        LinkPageStore.instance.addEventListener("link-removed", this.linkRemovedHandler);


    }

    disconnectedCallback() {
        // teardown event
        this.removeEventListener("selected", this.itemSelectHandler);
        this.removeEventListener("deselected", this.itemDeselectHandler);
        BulkLinkStore.instance.removeEventListener("select-all-requested", this.selectAllHandler);
        BulkLinkStore.instance.removeEventListener("selected", this.selectHandler);
        BulkLinkStore.instance.removeEventListener("deselected", this.deselectHandler);

        LinkPageStore.instance.removeEventListener("link-added", this.linkAddedHandler);
        LinkPageStore.instance.removeEventListener("link-updated", this.linkUpdatedHandler);
        LinkPageStore.instance.removeEventListener("link-removed", this.linkRemovedHandler);

        // other cleanup
        this.serializedEventQueue?.destroy();
    }

    // all the stuff that is also handled by the server
    private render = async () => {
        let stack;
        if (this.criteria.create) {
            stack = {

            }
            // render initial view
            this.insertAdjacentHTML("afterbegin", this.view(stack, "create"));
            return;
        } else {
            stack = await StackStore.instance.getStackBySlug(this.userId, this.criteria.stack);
            this.showStackIndicators = stack.slug === "links";

            // render initial view
            this.insertAdjacentHTML("afterbegin", this.view(stack, "view"));
        }

        // render links
        const linkPage = await LinkPageStore.instance.getView(this.criteriaWatcher.data);

        // no search results?
        if (this.criteria.query && linkPage.links.length === 0) {
            this.innerHTML = this.noResultsView();
            return;
        }

        this.linkArea = this.querySelector("[name=link-area]");
        if (linkPage.links.length > 0)
            this.linkArea.innerHTML = this.linkPageView(linkPage);
    }

    private async scrollToElement() {
        if (!this.jumpToLinkId)
            return;

        setTimeout(() => {
            window.location.hash = "#_";
            setTimeout(() => {
                window.location.hash = `#${this.jumpToLinkId}`;
            }, 100);
        }, 100);
    }

    private async scrollCheck() {
        const route = Routing.instance.route;
        let hash = route.hash || window.location.hash;
        if (hash && hash.indexOf("#") === 0)
            hash = hash.substring(1);

        if (!hash || hash === "")
            return true;

        const page = await LinkPageStore.instance.getPage(hash);

        if (!isNaN(page)) {
            if (page !== this.criteria.page) {
                const newCriteria = this.criteria;
                newCriteria.page = page;
                const newUrl = LinkSession.buildUrl(newCriteria);
                Routing.instance.go(newUrl.toString(), "LinkPageElement");
                return false;
            } else {
                this.jumpToLinkId = hash;
            }
        }

        return true;
    }

    private get criteria(): LinkPageCriteria {
        return this.criteriaWatcher.data;
    }

    private linkRemovedHandler = (event: CustomEvent<Link>) => this.querySelector(`link-control[id='${event.detail.id}']`)?.remove();

    private linkAddedHandler = async (event: CustomEvent<Link>) => {
        const link = event.detail;

        this.serializedEventQueue.add(async () => {

            if (!this.criteria.stack || this.criteria.stack !== link.stack)
                return;

            const howToAddLinks = <HTMLElement>document.querySelector("tip-display");
            if (howToAddLinks && howToAddLinks.remove)
                howToAddLinks.remove();

            const element = this.querySelector(`link-control[id="${link.id}"]`);

            if (!element) {
                this.linkArea.insertAdjacentHTML("afterbegin", this.linkView(link));
            }
        });
    };

    private linkUpdatedHandler = async (event: CustomEvent<Link>) => {
        const link = event.detail;
        this.serializedEventQueue.add(async () => {
            const element = this.querySelector(`link-control[id="${link.id}"]`);

            if (!element)
                return;

            element.toggleAttribute("allow-edit", link.userId === this.userId);
            const jsonElement = element.querySelector("script[type='application/json+link']") as HTMLScriptElement;
            jsonElement.innerText = JSON.stringify(link);
        });
    };

    private selectAllHandler = () => {

        const links = Array.from(this.querySelectorAll("link-control"))
            .map(m => m.querySelector("script[type='application/json+link']") as HTMLScriptElement)
            .map(m => JSON.parse(m.innerText) as Link);

        BulkLinkStore.instance.selectMany(links);
    }

    private itemSelectHandler = (event: CustomEvent<Link>) => BulkLinkStore.instance.select(event.detail);

    private itemDeselectHandler = (event: CustomEvent<Link>) => BulkLinkStore.instance.deselect(event.detail);

    private selectHandler = (event: CustomEvent<Link[]>) => {
        event.detail.forEach(link => this.querySelector(`link-control[id="${link.id}"]`)?.setAttribute("marked", ""));
    }

    private deselectHandler = (event: CustomEvent<Link[]>) =>
        event.detail.forEach(link => this.querySelector(`link-control[id="${link.id}"]`)?.removeAttribute("marked"));

    private linkView = (link: Link) => this.escape`
        <link-control id="${link.id}"${this.showStackIndicators ? " show-stack" : ""}${this.userId === link.userId ? " allow-edit" : ""}>
            <script type="application/json+link">$${JSON.stringify(link)}</script>
        </link-control>
    `;

    private linkPageView = (linkPage: LinkPage) => `
        ${linkPage.links.map(link => this.linkView(link)).join("")}
        <page-picker page-count=${linkPage.pageCount}>
            <script type="application/json+link-page-criteria">${this.criteriaWatcher.json}</script>
        </page-picker>
    `;

    private view = (stack: Linkstack, action: "view" | "create") => this.escape`
        
        <stack-header 
            stack-id="${stack.id}"
            stack-action=${action}
            stack-title="${stack.title || ""}"
            stack-description="${stack.description || ""}"
            stack-slug="${stack.slug}"
            can-edit="${stack.canEdit}"
            ${stack.private ? "stack-private" : ""}
            collab-count="${stack.collaboratorCount}"></stack-header>

            $${action === "view" ? this.escape`
                <div name=link-area>
                    $${stack.linkCount === 0 ? `<tip-display></tip-display>` : ""}
                    $${this.linkSkeletons(stack.linkCount)}
                </div>
        `: ""} 
    `;

    private noResultsView = () => `
        <div name=no-results>
            ${require("!!raw-loader!image/no-results.svg")}
            <div>No results</div>
        </div>
    `;

    private linkSkeletons = (count: number) => {
        const items = [...Array(count).keys()];
        return items.map(_ => "<link-skeleton></link-skeleton>").join("");
    };
}

customElements.define("link-page", LinkPageElement);